import { Cookie, SetCookie } from '@pulse/common';

export class CookieParser {
    /**
     * Parses HTTP Cookie header value.
     */
    parseCookies(str: string): Cookie[] {
        const cookies: Cookie[] = [];
        const items: string[] = str.split(';');

        for (const item of items) {
            const cookie: Cookie = <Cookie>{};
            const eqSignIndex: number = item.indexOf('=');

            if (eqSignIndex > -1) {
                cookie.name = item.slice(0, eqSignIndex).trim() || '';
                cookie.value = item.slice(eqSignIndex + 1).trim();
            } else {
                cookie.name = '';
                cookie.value = item.trim();
            }

            if (!cookie.name) {
                continue;
            }

            cookie.name = (cookie.name || '').replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);
            cookie.value = (cookie.value || '').replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);

            cookies.push(cookie);
        }

        return cookies;
    }

    /**
     * Serialize cookie object into string suitable for HTTP Set-Cookie header.
     */
    serializeSetCookie(cookie: SetCookie): string {
        if (!cookie.name || typeof cookie.name !== 'string') {
            throw new Error('Invalid cookie name!');
        }

        const ctx: SetCookie = { ...cookie };

        const name = encodeURIComponent(ctx.name)
            .replace(/%(2[346B]|5E|60|7C)/g, decodeURIComponent)
            .replace(/[()]/g, escape);
        const value: string = encodeURIComponent(ctx.value || '').replace(
            /%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
            decodeURIComponent,
        );
        const expires: string | undefined = this.serializeExpires(ctx.expires);
        const parts: string[] = [`${name}=${value}`];

        if (typeof expires === 'string') {
            parts.push(`Expires=${expires}`);
        }

        if (typeof ctx.domain === 'string') {
            parts.push(`Domain=${ctx.domain}`);
        }

        parts.push(`Path=${ctx.path || '/'}`);

        if (typeof cookie.sameSite === 'string') {
            const upperSameSite: string = cookie.sameSite.toUpperCase();

            if (upperSameSite === 'LAX') {
                parts.push('SameSite=Lax');
            } else if (upperSameSite === 'STRICT') {
                parts.push('SameSite=Strict');
            } else if (upperSameSite === 'NONE') {
                parts.push('SameSite=None');
                ctx.secure = true;
            }
        }

        if (ctx.secure === true) {
            parts.push('Secure');
        }

        return parts.join('; ');
    }

    /**
     * Serialize Cookie.expires into date string suitable for Expires attribute.
     *
     * @param {(number | undefined)} expires milliseconds elapsed since the UNIX epoch.
     * @returns {(string | undefined)} Returns serialized Expires attribute value.
     * Returning undefined causes the parser to ignore this attribute.
     */
    serializeExpires(expires?: number): string | undefined {
        if (Number.isSafeInteger(<number>expires)) {
            const date: Date = new Date(<number>expires);

            if (!isNaN(Number(date))) {
                return date.toUTCString();
            }
        }

        return undefined;
    }

    /**
     * Function used for converting Expires attribute to Cookie.expires,
     * date in milliseconds starting from January 1, 1970 00:00:00 UTC
     *
     * @param {string} expires Expires attribute value
     */
    parseExpires(expires: string | undefined): number | undefined {
        if (typeof expires === 'string') {
            const result: number = Date.parse(expires);

            if (Number.isSafeInteger(result)) {
                return result;
            }
        }

        return undefined;
    }
}
